import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPen, FaPercent, FaPlus } from 'react-icons/fa';
import { HiArrowTrendingUp } from 'react-icons/hi2';
import { GiGears } from 'react-icons/gi';
import { exploreUnknownXpBarPercent } from './utils/exploreUnknownXpBarPercent';
import {
  replaceFirstMetricInTitle,
  resolveExploreUnknownErraySlotLabels,
} from './utils/exploreUnknownLevelTreeSlotDisplay';
import { getExploreUnknownSurfaceFirstMissionPreviewUrl } from './config/exploreUnknownMissionBattle';
import {
  getDiplomaticAccordPicklistOption,
  getSecondDiplomaticAccordPicklistOptions,
  REACTIVE_DOCTRINE_LEVEL,
} from './config/exploreUnknownDiplomaticAccordPicklist';
import { playSoundEffect } from './hooks/useSoundEffects';
import './ExploreUnknownLevelTree.css';

const EXPLORE_UNKNOWN_SLOT_ERRAY_SOUND = '/sounds/slot_erray.mp3';
const EXPLORE_UNKNOWN_SELECT_SKILL_SOUND = '/sounds/select_skill.mp3';

function resolveOptionalSkipTileIcon(def) {
  const startSurface = def?.effects?.startSurface;
  if (!startSurface) {
    return def?.icon ?? null;
  }
  try {
    return getExploreUnknownSurfaceFirstMissionPreviewUrl(startSurface);
  } catch {
    return def?.icon ?? null;
  }
}

/** Locked L50+ tiles show subdued art preview; corner badges stay hidden until unlock. */
/** Legacy v1 milestone bonusId values (green up-arrow). */
const MILESTONE_UP_ARROW_BONUS_IDS = new Set([
  'memoryBonusDarkness',
  'heartHealthBonus',
  'epFewerFragments',
  'rewriteFewerFragments',
  'singleFragment',
]);

/** Legacy v1 auto-collect milestone bonusIds (white cogs). */
const MILESTONE_AUTO_COLLECT_BONUS_IDS = new Set([
  'autoCollectMemory',
  'autoCollectDarkKnowledge',
  'autoCollectRewrite',
  'autoCollectEp',
]);

/** Legacy v1 health / darkness milestone bonusIds (green plus signs). */
const MILESTONE_HEALTH_PLUS_BONUS_IDS = new Set(['darknessReduction', 'healthPerWin']);

const BOOST_EFFECT_KINDS = new Set([
  'memoryDarknessMultiplier',
  'memoryVariantBonus',
  'heartExtraHealth',
  'heartYieldMultiplier',
  'excessHealthConversion',
  'fragmentPersistTurns',
  'rewriteBossRetry',
  'rewriteSpendHealthBonus',
  'nextMissionXpMultiplier',
]);

/** Small round tiles that raise proc/spawn odds (green % corner badge). */
const CHANCE_EFFECT_KINDS = new Set([
  'mapProc',
  'winProcDarknessReduction',
  'healthPerWinProc',
  'fragmentDoubleChance',
  'singleFragmentProc',
  'epUpgradeChance',
  'epZoneKeepUnits',
  'epSingleKeepCharge',
  'memoryDarknessDoubleChance',
  'rewriteKeepCharge',
  'worldEntryReward',
  'bossKillReward',
  'spawnWeightDelta',
  'spawnWeightSet',
]);

const AUTO_EFFECT_KINDS = new Set(['autoCollect', 'autoCollectMode']);

const HEALTH_EFFECT_KINDS = new Set([
  'darknessReductionPerWin',
  'healthPerWin',
  'healthPerWinConditional',
  'heartCompleteDarknessReduction',
]);

function isSmallRoundLevelTreeTile(def) {
  const type = def?.type;
  return type === 'variant' || type === 'selectOne';
}

function isChanceLevelTreeEffect(def, meta, effects) {
  const kind = effects?.kind;
  if (kind && CHANCE_EFFECT_KINDS.has(kind)) return true;
  if (kind === 'skipWorldOption' && effects?.skipTwoWorldsChance != null) return true;

  const bonusName = String(def?.bonusName || meta?.bonusName || '').toLowerCase();
  if (/\d+%\s*chance/.test(bonusName) || /chance.*\d+%/.test(bonusName)) return true;
  if (/chance by \d+%/.test(bonusName) || /increase.*chance/.test(bonusName)) return true;

  return false;
}

/**
 * @param {object} def
 * @param {object} meta
 * @returns {'chance'|'boost'|'auto'|'health'|null}
 */
function resolveLevelTreeCornerBadge(def, meta) {
  const effects = def?.effects || meta?.effects;
  const kind = effects?.kind;
  if (isSmallRoundLevelTreeTile(def) && isChanceLevelTreeEffect(def, meta, effects)) {
    return 'chance';
  }
  if (kind && BOOST_EFFECT_KINDS.has(kind)) return 'boost';
  if (kind && AUTO_EFFECT_KINDS.has(kind)) return 'auto';
  if (kind && HEALTH_EFFECT_KINDS.has(kind)) return 'health';

  const bonusId = String(def?.bonusId || meta?.bonusId || '');
  if (MILESTONE_UP_ARROW_BONUS_IDS.has(bonusId)) return 'boost';
  if (MILESTONE_AUTO_COLLECT_BONUS_IDS.has(bonusId)) return 'auto';
  if (MILESTONE_HEALTH_PLUS_BONUS_IDS.has(bonusId)) return 'health';

  const bonusName = String(def?.bonusName || meta?.bonusName || '').toLowerCase();
  if (/fewer/.test(bonusName) && /fragment/.test(bonusName)) return 'boost';
  if (/automatic/.test(bonusName) && /collect/.test(bonusName)) return 'auto';

  return null;
}

function cornerIconSize(tileSize, ratio = 0.31) {
  return Math.max(10, Math.round(tileSize * ratio));
}

/** Three chevron paths (viewBox 0 0 60 60) — skip-world level tree art. */
const SKIP_ARROW_PATHS = Object.freeze([
  'M5 22 L30 6 L55 22 L48 27 L30 16 L12 27 Z',
  'M5 38 L30 22 L55 38 L48 43 L30 32 L12 43 Z',
  'M5 54 L30 38 L55 54 L48 59 L30 48 L12 59 Z',
]);

const ENDGAME_STAR_POINTS = '50,2 61,36 98,36 68,58 79,92 50,72 21,92 32,58 2,36 39,36';

function EndgameStarTileArt({ icon, patternId }) {
  const pid = `eu-star-${String(patternId || 'default').replace(/[^a-zA-Z0-9_-]/g, '_')}`;
  const clipId = `${pid}-clip`;

  return (
    <svg
      className="eu-endgame-star-svg"
      viewBox="0 0 100 100"
      aria-hidden
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <clipPath id={clipId}>
          <polygon points={ENDGAME_STAR_POINTS} />
        </clipPath>
      </defs>
      <polygon points={ENDGAME_STAR_POINTS} className="eu-endgame-star-fill" />
      {icon ? (
        <image
          href={icon}
          x="0"
          y="0"
          width="100"
          height="100"
          clipPath={`url(#${clipId})`}
          className="eu-endgame-star-icon"
          preserveAspectRatio="xMidYMid slice"
        />
      ) : null}
      <polygon points={ENDGAME_STAR_POINTS} className="eu-endgame-star-stroke" fill="none" />
    </svg>
  );
}

function SkipWorldTileArt({ icon, patternId }) {
  const pid = `eu-skip-${String(patternId || 'default').replace(/[^a-zA-Z0-9_-]/g, '_')}`;

  return (
    <svg
      className="eu-level-tree-skip-svg"
      viewBox="0 0 60 60"
      aria-hidden
      preserveAspectRatio="xMidYMid meet"
    >
      {icon ? (
        <defs>
          <pattern id={pid} patternUnits="userSpaceOnUse" width="60" height="60">
            <image href={icon} x="0" y="0" width="60" height="60" preserveAspectRatio="xMidYMid slice" />
          </pattern>
        </defs>
      ) : null}
      {SKIP_ARROW_PATHS.map((d, i) => (
        <g key={i} className="eu-level-tree-skip-arrow-group">
          <path
            d={d}
            className="eu-level-tree-skip-arrow-fill"
            fill={icon ? `url(#${pid})` : 'rgba(30, 41, 59, 0.92)'}
          />
          <path d={d} className="eu-level-tree-skip-arrow-stroke" fill="none" />
        </g>
      ))}
    </svg>
  );
}

/** Major anchor + optional circles — preparation (L1) only. */
const ANCHOR_VARIANT_UI = Object.freeze({
  prepBonus: Object.freeze({ anchorLevel: 1, variantLevels: [2, 3, 4] }),
});
const PREP_ANCHOR_LEVEL = 1;
const LEVEL6_OR7_PICK_GROUP = 'diplomaticAccordLevel6Pick';
const ALT_PATH_SELECT_GROUP = 'altPathSelect20';
const ALT_PATH_SECOND_SELECT_KEY = 'altPathSelect20Second';
const ALT_PATH_L21_BONUS_ID = 'alt_path_boss_retry_darkness_reduction_l21';

function isPrepAnchorLevel(lv) {
  return Number(lv) === PREP_ANCHOR_LEVEL;
}

function isAnchorMajorLevel(lv) {
  return isPrepAnchorLevel(lv);
}

function isAnchorVariantCircle(def) {
  return Boolean(def?.groupId && def.type === 'variant' && ANCHOR_VARIANT_UI[def.groupId]);
}

function isPrepAnchor(def, lv) {
  return isPrepAnchorLevel(lv ?? def?.level);
}

function isAnchorVariantAnchor(def, lv) {
  return isPrepAnchor(def, lv);
}

function isCycleSelectTile(def) {
  return def?.cycleSelect === true && def?.groupId === 'diplomaticAccordFirst';
}

function isSecondAccordCircle(def, lv) {
  return Number(lv ?? def?.level) === 7 && def?.cycleSelectKey === 'diplomaticAccordSecond';
}

function isAltPathSelectTile(def) {
  return def?.groupId === ALT_PATH_SELECT_GROUP && def?.type === 'selectOne';
}

function isAltPathL21ErraySlotted(refinedErrayAttachments) {
  return refinedErrayAttachments?.[ALT_PATH_L21_BONUS_ID] === true;
}

function isAltPathSecondPickActive(def, bonusSelections) {
  if (!isAltPathSelectTile(def) || def.optionIndex === 0) return false;
  const raw = bonusSelections?.[ALT_PATH_SECOND_SELECT_KEY];
  if (raw == null || raw === '') return false;
  return Number(raw) === def.optionIndex;
}

function getLevel6Or7Pick(bonusSelections) {
  const raw = bonusSelections?.[LEVEL6_OR7_PICK_GROUP];
  if (raw == null || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function resolveCycleSelectPick(groupId, bonusSelections) {
  if (groupId === 'diplomaticAccordFirst') {
    const raw = bonusSelections?.diplomaticAccordFirst ?? bonusSelections?.diplomaticAccords;
    const idx = raw != null && raw !== '' ? Number(raw) : 0;
    return getDiplomaticAccordPicklistOption(Number.isFinite(idx) ? idx : 0);
  }
  if (groupId === 'diplomaticAccordSecond') {
    const firstRaw = bonusSelections?.diplomaticAccordFirst ?? bonusSelections?.diplomaticAccords;
    const firstIdx = firstRaw != null && firstRaw !== '' ? Number(firstRaw) : 0;
    const available = getSecondDiplomaticAccordPicklistOptions(Number.isFinite(firstIdx) ? firstIdx : 0);
    const rawSecond = bonusSelections?.diplomaticAccordSecond;
    const relIdx = rawSecond != null && rawSecond !== '' ? Number(rawSecond) : 0;
    return available[Number.isFinite(relIdx) ? relIdx : 0] || available[0] || null;
  }
  return null;
}

function nextCycleSelectIndex(groupId, bonusSelections) {
  if (groupId === 'diplomaticAccordFirst') {
    const raw = bonusSelections?.diplomaticAccordFirst ?? bonusSelections?.diplomaticAccords;
    const current = raw != null && raw !== '' ? Number(raw) : 0;
    return Number.isFinite(current) ? (current + 1) % 3 : 0;
  }
  if (groupId === 'diplomaticAccordSecond') {
    const firstRaw = bonusSelections?.diplomaticAccordFirst ?? bonusSelections?.diplomaticAccords;
    const firstIdx = firstRaw != null && firstRaw !== '' ? Number(firstRaw) : 0;
    const available = getSecondDiplomaticAccordPicklistOptions(Number.isFinite(firstIdx) ? firstIdx : 0);
    const rawSecond = bonusSelections?.diplomaticAccordSecond;
    const current = rawSecond != null && rawSecond !== '' ? Number(rawSecond) : 0;
    if (available.length === 0) return 0;
    return Number.isFinite(current) ? (current + 1) % available.length : 0;
  }
  return 0;
}

function resolveSecondAccordPick(bonusSelections) {
  return resolveCycleSelectPick('diplomaticAccordSecond', bonusSelections);
}

function isReactiveDoctrineCircle(def, lv) {
  return Number(lv ?? def?.level) === REACTIVE_DOCTRINE_LEVEL && def?.effects?.kind === 'reactiveDoctrine';
}

function getAnchorVariantSelectionIndex(bonusSelections, groupId) {
  const sel = bonusSelections?.[groupId];
  if (sel == null || sel === '') return null;
  const n = Number(sel);
  return Number.isFinite(n) ? n : null;
}

function resolveAnchorVariantDisplayDef(groupId, defsByLevel, bonusSelections) {
  const cfg = ANCHOR_VARIANT_UI[groupId];
  if (!cfg) return null;
  const anchor = defsByLevel[String(cfg.anchorLevel)];
  const sel = getAnchorVariantSelectionIndex(bonusSelections, groupId);
  if (sel == null) return anchor;
  for (const lv of cfg.variantLevels) {
    const d = defsByLevel[String(lv)];
    if (d && d.optionIndex === sel) return d;
  }
  return anchor;
}

const LEVELS = Array.from({ length: 100 }, (_, i) => i + 1);
const ENDGAME_STAR_LEVELS = Object.freeze([96, 97, 98, 99]);
const CAPSTONE_LEVEL = 100;
const CAPSTONE_TILE_SCALE = 1.3;
/** Path never connects to level 100 (capstone sits on its own row). */
const FINALE_PATH_MAX_LEVEL_INDEX = 98;
/** Tiles per snake row (levels 1–5 on row 0, 6–10 on row 1, …). */
const COLS = 5;
/** Snake rows 0–18 hold levels 1–95 (unchanged spacing vs the original 20-row tree). */
const SNAKE_ROWS = 19;
/** Dedicated row for endgame stars 96–99 (same Y step as the old level-96 row). */
const FINALE_STAR_ROW = 19;
/** Level 100 alone on the next row below the stars. */
const FINALE_CAPSTONE_ROW = 20;
/** Total row bands for Y layout (snake + star row + capstone row). */
const ROWS = 21;
const MIN_TILE = 56;
const MAX_TILE = 128;
const GAP_X_RATIO = 34 / 72;
const GAP_Y_RATIO = 54 / 72;
const PAD_X_RATIO = 20 / 72;
const PAD_Y_RATIO = 28 / 72;
/** Minimum horizontal space between tile slots in a row. */
const MIN_COLUMN_GAP_RATIO = 14 / 72;
/** Minimum vertical gap between snake rows (row-turn spacing). */
const MIN_ROW_GAP_RATIO = 22 / 72;
/** Refined erray pocket below tile: art height at 48px display scale (~14px) + gap. */
const ERRAY_SLOT_BELOW_TILE = 22;
/** Extra multiplier on row-turn vertical gaps (between horizontal runs). */
const ROW_TURN_GAP_SCALE = 1.2;
/** Row turns never use the tight circle–circle vertical scale (keeps rows readable). */
const ROW_TURN_MIN_GAP_SCALE = 0.62;

/** @returns {{ tile: number, gapX: number, gapY: number, padX: number, padY: number, boardW: number, boardH: number, gapXBonus?: number }} */
function layoutFromTile(tile) {
  const gapX = Math.round(tile * GAP_X_RATIO);
  const gapY = Math.round(tile * GAP_Y_RATIO);
  const padX = Math.round(tile * PAD_X_RATIO);
  const padY = Math.round(tile * PAD_Y_RATIO);
  const boardW = padX * 2 + COLS * tile + (COLS - 1) * gapX;
  const boardH = padY * 2 + ROWS * tile + (ROWS - 1) * gapY;
  return { tile, gapX, gapY, padX, padY, boardW, boardH };
}

function computeBoardLayout(availW, availH, { isMobile = false } = {}) {
  const width = Math.max(240, Number(availW) || 480);
  const widthUnits = PAD_X_RATIO * 2 + COLS + (COLS - 1) * GAP_X_RATIO;
  const tileFromWidth = width / widthUnits;
  const minTile = isMobile ? 40 : MIN_TILE;
  const maxTile = isMobile ? 64 : MAX_TILE;
  const tile = Math.floor(Math.max(minTile, Math.min(maxTile, tileFromWidth)));
  return layoutFromTile(tile);
}

/**
 * Circle / lore columns are narrower than the uniform width model; leftover scroll width
 * is distributed evenly across column gaps so the tree uses the panel without scrolling.
 */
function isEndgameStarLevel(lv) {
  return lv >= 96 && lv <= 99;
}

function estimateBoardWidth(layout) {
  const gapXBonus = layout.gapXBonus || 0;
  return layout.padX * 2 + COLS * layout.tile + (COLS - 1) * (layout.gapX + gapXBonus);
}

function computeEndgameStarTileSize(layout) {
  const boardW = estimateBoardWidth(layout);
  const innerW = boardW - layout.padX * 2;
  const gap = Math.max(
    layout.gapX + (layout.gapXBonus || 0),
    Math.round(layout.tile * MIN_COLUMN_GAP_RATIO)
  );
  return Math.min(
    Math.round(layout.tile * 1.5),
    Math.max(layout.tile, Math.floor((innerW - gap * 3) / 4))
  );
}

function fitLayoutToAvailableWidth(layout, defsByLevel, availW) {
  const width = Math.max(240, Number(availW) || 0);
  const { boardW } = computeTreeGeometry(layout, defsByLevel);
  const slack = Math.max(0, Math.floor(width - boardW));
  let next = layout;
  if (slack >= 2 && COLS > 1) {
    const gapXBonus = Math.floor(slack / (COLS - 1));
    next = { ...layout, gapXBonus };
  }
  return {
    ...next,
    endgameTile: computeEndgameStarTileSize(next),
    capstoneTile: Math.round(next.tile * CAPSTONE_TILE_SCALE),
  };
}

function computeBoardLayoutForTree(availW, availH, defsByLevel, { isMobile = false } = {}) {
  const base = computeBoardLayout(availW, availH, { isMobile });
  return fitLayoutToAvailableWidth(base, defsByLevel, availW);
}

/** Circle nodes (variant, selectOne) render at 40% of major tile size. */
const MINOR_TILE_SCALE = 0.4;
/** Lore / alternate-art nodes: rounded squares between circle and major size. */
const LORE_ART_TILE_SCALE = 0.68;

function tileSizeForType(type, baseTile) {
  if (type === 'major' || type === 'optionalSkip' || type === 'endgame') {
    return baseTile;
  }
  if (type === 'loreArt') {
    return Math.max(36, Math.round(baseTile * LORE_ART_TILE_SCALE));
  }
  return Math.max(28, Math.round(baseTile * MINOR_TILE_SCALE));
}

/** Tighter gaps between circle nodes (variant / selectOne). */
const MINOR_GAP_SCALE = 0.28;
const LORE_GAP_SCALE = 0.58;

function isCircleMinor(type) {
  return type === 'variant' || type === 'selectOne';
}

function isLoreArtTile(type) {
  return type === 'loreArt';
}

function isMinorShape(type) {
  return isCircleMinor(type);
}

function typeAtIndex(index, defsByLevel) {
  const def = defsByLevel[String(index + 1)] || {};
  return def.type || 'major';
}

function tileSizeForDef(def, baseTile, lv, layout = null) {
  if (isAnchorMajorLevel(lv)) {
    return baseTile;
  }
  if (layout) {
    if (lv === CAPSTONE_LEVEL) {
      return layout.capstoneTile ?? Math.round(baseTile * CAPSTONE_TILE_SCALE);
    }
    if (isEndgameStarLevel(lv)) {
      return layout.endgameTile ?? computeEndgameStarTileSize(layout);
    }
  }
  return tileSizeForType(def.type || 'major', baseTile);
}

/** Map grid (col, row) to linear level index; even rows LTR, odd rows RTL. */
function indexForColRow(col, row) {
  const within = row % 2 === 0 ? col : COLS - 1 - col;
  return row * COLS + within;
}

function levelIndexToColRow(index) {
  const row = Math.floor(index / COLS);
  const within = index % COLS;
  const col = row % 2 === 0 ? within : COLS - 1 - within;
  return { col, row };
}

/** Level index at the end of a snake row and the start of the next (for row-turn spacing). */
function snakeRowTurnIndices(row) {
  const endCol = row % 2 === 0 ? COLS - 1 : 0;
  const startCol = (row + 1) % 2 === 0 ? 0 : COLS - 1;
  return {
    from: indexForColRow(endCol, row),
    to: indexForColRow(startCol, row + 1),
  };
}

function gapScaleForTypes(typeA, typeB) {
  if (isCircleMinor(typeA) && isCircleMinor(typeB)) return MINOR_GAP_SCALE;
  if (isCircleMinor(typeA) || isCircleMinor(typeB)) {
    if (isLoreArtTile(typeA) || isLoreArtTile(typeB)) return (MINOR_GAP_SCALE + LORE_GAP_SCALE) / 2;
    return (MINOR_GAP_SCALE + 1) / 2;
  }
  if (isLoreArtTile(typeA) && isLoreArtTile(typeB)) return LORE_GAP_SCALE;
  if (isLoreArtTile(typeA) || isLoreArtTile(typeB)) return (LORE_GAP_SCALE + 1) / 2;
  return 1;
}

/** Vertical distance between snake rows (generous; not the tight within-row circle scale). */
function verticalRowTurnStep(layout, defsByLevel, indexA, indexB) {
  const typeA = typeAtIndex(indexA, defsByLevel);
  const typeB = typeAtIndex(indexB, defsByLevel);
  const defA = defsByLevel[String(indexA + 1)] || {};
  const defB = defsByLevel[String(indexB + 1)] || {};
  const tileA = tileSizeForDef(defA, layout.tile, indexA + 1, layout);
  const tileB = tileSizeForDef(defB, layout.tile, indexB + 1, layout);
  const gapScale = Math.max(ROW_TURN_MIN_GAP_SCALE, gapScaleForTypes(typeA, typeB));
  const gap = Math.max(
    Math.round(layout.tile * MIN_ROW_GAP_RATIO),
    Math.round(layout.gapY * gapScale * ROW_TURN_GAP_SCALE)
  );
  return tileA / 2 + gap + tileB / 2;
}

function maxTileInColSlot(col, layout, defsByLevel) {
  let maxTile = 0;
  for (let row = 0; row < SNAKE_ROWS; row += 1) {
    const idx = indexForColRow(col, row);
    maxTile = Math.max(maxTile, tileSizeForDef(defsByLevel[String(idx + 1)] || {}, layout.tile, idx + 1, layout));
  }
  return maxTile;
}

function horizontalGapBetweenColSlots(layout, defsByLevel, col) {
  if (col >= COLS - 1) return 0;
  let minorsLeft = 0;
  let minorsRight = 0;
  for (let row = 0; row < SNAKE_ROWS; row += 1) {
    if (isMinorShape(typeAtIndex(indexForColRow(col, row), defsByLevel))) minorsLeft += 1;
    if (isMinorShape(typeAtIndex(indexForColRow(col + 1, row), defsByLevel))) minorsRight += 1;
  }
  const typeLeft = minorsLeft > ROWS / 2 ? 'variant' : 'major';
  const typeRight = minorsRight > ROWS / 2 ? 'variant' : 'major';
  const scaled = layout.gapX * gapScaleForTypes(typeLeft, typeRight);
  const minGap = Math.round(layout.tile * MIN_COLUMN_GAP_RATIO);
  const base = Math.max(minGap, scaled);
  return base + (layout.gapXBonus || 0);
}

function computeColumnCenterX(layout, defsByLevel) {
  const xs = [];
  let x = layout.padX + maxTileInColSlot(0, layout, defsByLevel) / 2;
  xs[0] = x;
  for (let col = 1; col < COLS; col += 1) {
    x += maxTileInColSlot(col - 1, layout, defsByLevel) / 2;
    x += horizontalGapBetweenColSlots(layout, defsByLevel, col - 1);
    x += maxTileInColSlot(col, layout, defsByLevel) / 2;
    xs[col] = x;
  }
  return xs;
}

function computeRowCenterY(layout, defsByLevel) {
  const ys = [];
  const firstIdx = indexForColRow(0, 0);
  ys[0] =
    layout.padY + tileSizeForDef(defsByLevel[String(firstIdx + 1)] || {}, layout.tile, firstIdx + 1, layout) / 2;
  for (let row = 1; row < ROWS; row += 1) {
    let from;
    let to;
    if (row === FINALE_CAPSTONE_ROW) {
      from = CAPSTONE_LEVEL - 2;
      to = CAPSTONE_LEVEL - 1;
    } else {
      ({ from, to } = snakeRowTurnIndices(row - 1));
    }
    ys[row] = ys[row - 1] + verticalRowTurnStep(layout, defsByLevel, from, to);
  }
  return ys;
}

/** Stars 96–99 share one row Y (old level-96 row); capstone 100 uses the next row Y. X only is custom. */
function applyFinaleLayout(centers, layout, rowY) {
  const boardW = estimateBoardWidth(layout);
  const innerW = boardW - layout.padX * 2;

  ENDGAME_STAR_LEVELS.forEach((lv, i) => {
    const frac = (i + 0.5) / ENDGAME_STAR_LEVELS.length;
    centers[lv - 1] = {
      col: i,
      row: FINALE_STAR_ROW,
      x: layout.padX + innerW * frac,
      y: rowY[FINALE_STAR_ROW],
    };
  });

  centers[CAPSTONE_LEVEL - 1] = {
    col: 2,
    row: FINALE_CAPSTONE_ROW,
    x: boardW / 2,
    y: rowY[FINALE_CAPSTONE_ROW],
  };
}

function computeTreeGeometry(layout, defsByLevel) {
  const columnX = computeColumnCenterX(layout, defsByLevel);
  const rowY = computeRowCenterY(layout, defsByLevel);

  const centers = LEVELS.map((_, index) => {
    if (index >= 95) {
      return { col: 0, row: 0, x: 0, y: 0 };
    }
    const { col, row } = levelIndexToColRow(index);
    return { col, row, x: columnX[col], y: rowY[row] };
  });

  applyFinaleLayout(centers, layout, rowY);

  let boardW = layout.padX;
  let boardH = layout.padY;
  for (let index = 0; index < centers.length; index += 1) {
    const c = centers[index];
    const def = defsByLevel[String(index + 1)] || {};
    const tile = tileSizeForDef(def, layout.tile, index + 1, layout);
    let bottom = c.y + tile / 2;
    if (def.refinedErraySlot) {
      bottom += ERRAY_SLOT_BELOW_TILE;
    }
    boardW = Math.max(boardW, c.x + tile / 2);
    boardH = Math.max(boardH, bottom);
  }
  boardW += layout.padX;
  boardH += layout.padY;

  return { centers, boardW, boardH };
}

/**
 * Snake path for levels 1–95, then a horizontal run through endgame stars 96–99 (never level 100).
 * @param {Array<{x:number,y:number}>} centers
 * @param {number} maxLevelIndexInclusive 0-based index (max 98 = level 99)
 */
function buildTreePath(centers, maxLevelIndexInclusive) {
  const cap = Math.min(maxLevelIndexInclusive, FINALE_PATH_MAX_LEVEL_INDEX);
  if (cap < 94) {
    return buildSnakePath(centers.slice(0, cap + 1));
  }
  let path = buildSnakePath(centers.slice(0, 95));
  if (cap < 95) {
    return path;
  }
  for (let i = 95; i <= cap; i += 1) {
    path += ` L ${centers[i].x} ${centers[i].y}`;
  }
  return path;
}

function buildSnakePath(points) {
  const parts = [];
  points.forEach((p, i) => {
    if (i === 0) {
      parts.push(`M ${p.x} ${p.y}`);
      return;
    }
    const prev = points[i - 1];
    if (prev.y === p.y) {
      parts.push(`L ${p.x} ${p.y}`);
    } else if (prev.x === p.x) {
      parts.push(`L ${p.x} ${p.y}`);
    } else {
      const midY = (prev.y + p.y) / 2;
      parts.push(`C ${prev.x} ${midY}, ${p.x} ${midY}, ${p.x} ${p.y}`);
    }
  });
  return parts.join(' ');
}

function shapeClassForType(type) {
  if (type === 'major') return 'eu-level-tree-tile--shape-major';
  if (type === 'loreArt') return 'eu-level-tree-tile--shape-lore';
  if (type === 'optionalSkip') return 'eu-level-tree-tile--shape-skip';
  if (type === 'endgame') return 'eu-level-tree-tile--shape-endgame';
  return 'eu-level-tree-tile--shape-minor';
}

export default function ExploreUnknownLevelTree({
  level,
  xpIntoLevel,
  xpToNext,
  totalXp,
  milestoneBonuses = {},
  levelDefinitions = null,
  bonusSelections = {},
  endgameBonusId = null,
  skippingBonuses = {},
  refinedErrayCount = 0,
  refinedErraySpare = 0,
  refinedErrayAttachments = {},
  onBonusSelectionChange = null,
  onEndgameChange = null,
  onSkippingToggle = null,
  onAttachRefinedErray = null,
  reactiveDoctrineMetadata = null,
  onReactiveDoctrineConfigure = null,
  onBonusSelectionsPatch = null,
  isMobile = false,
}) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(null);
  const boardScrollRef = useRef(null);
  const [boardScrollSize, setBoardScrollSize] = useState({ width: 520, height: 720 });

  useEffect(() => {
    const el = boardScrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => {
      setBoardScrollSize({ width: el.clientWidth, height: el.clientHeight });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const defsByLevel = useMemo(() => {
    if (levelDefinitions && typeof levelDefinitions === 'object') {
      return levelDefinitions;
    }
    const out = {};
    for (const [lv, meta] of Object.entries(milestoneBonuses || {})) {
      out[lv] = { ...meta, level: Number(lv) };
    }
    return out;
  }, [levelDefinitions, milestoneBonuses]);

  const boardLayout = useMemo(
    () => computeBoardLayoutForTree(boardScrollSize.width, boardScrollSize.height, defsByLevel, { isMobile }),
    [boardScrollSize, defsByLevel, isMobile]
  );

  const treeGeometry = useMemo(
    () => computeTreeGeometry(boardLayout, defsByLevel),
    [boardLayout, defsByLevel]
  );
  const { centers, boardW, boardH } = treeGeometry;
  const path = useMemo(() => buildTreePath(centers, FINALE_PATH_MAX_LEVEL_INDEX), [centers]);
  const pathStrokeLocked = Math.max(6, Math.round(boardLayout.tile * 0.11));
  const pathStrokeUnlocked = Math.max(4, Math.round(boardLayout.tile * 0.07));
  const boardStyle = {
    width: `${boardW}px`,
    height: `${boardH}px`,
    '--eu-tile': `${boardLayout.tile}px`,
  };

  const xpPct = exploreUnknownXpBarPercent(xpIntoLevel, xpToNext);
  const totalXpDisplay = Number(totalXp || 0).toLocaleString();

  const lastUnlockedIdx = LEVELS.reduce((acc, lv, i) => (level >= lv ? i : acc), -1);
  const nextIdx = lastUnlockedIdx + 1 < LEVELS.length ? lastUnlockedIdx + 1 : -1;

  const isSelectedInGroup = (def) => {
    if (!def?.groupId || def.optionIndex == null) return false;
    const sel = bonusSelections[def.groupId];
    const idx = sel != null ? Number(sel) : 0;
    return idx === def.optionIndex;
  };

  const isActiveBonus = (def, unlocked, lv) => {
    if (!unlocked || !def) return false;
    if (isAnchorVariantAnchor(def, lv)) return true;
    if (isAnchorVariantCircle(def)) {
      const sel = getAnchorVariantSelectionIndex(bonusSelections, def.groupId);
      return sel != null && Number(sel) === Number(def.optionIndex);
    }
    if (def.type === 'endgame') return def.bonusId === endgameBonusId;
    if (def.type === 'optionalSkip') return skippingBonuses[def.bonusId] !== false;
    if (isReactiveDoctrineCircle(def, lv)) return getLevel6Or7Pick(bonusSelections) === 0;
    if (isSecondAccordCircle(def, lv)) return getLevel6Or7Pick(bonusSelections) === 1;
    if (isCycleSelectTile(def)) return true;
    if (def.type === 'variant' || def.type === 'selectOne') {
      if (isAltPathSelectTile(def) && isAltPathL21ErraySlotted(refinedErrayAttachments)) {
        if (def.optionIndex === 0) return true;
        return isAltPathSecondPickActive(def, bonusSelections);
      }
      return isSelectedInGroup(def);
    }
    return def.type === 'major' || def.type === 'loreArt';
  };

  const handleTileActivate = (def, unlocked) => {
    if (!unlocked || !def?.bonusId) return;

    if (isPrepAnchor(def, def.level)) {
      if (getAnchorVariantSelectionIndex(bonusSelections, 'prepBonus') != null) {
        onBonusSelectionChange?.('prepBonus', null);
        return;
      }
    }

    if (isCycleSelectTile(def)) {
      playSoundEffect(EXPLORE_UNKNOWN_SELECT_SKILL_SOUND);
      const nextIdx = nextCycleSelectIndex(def.groupId, bonusSelections);
      onBonusSelectionChange?.(def.groupId, nextIdx);
      if (def.groupId === 'diplomaticAccordFirst') {
        const secondRaw = bonusSelections?.diplomaticAccordSecond;
        const firstRaw = nextIdx;
        const available = getSecondDiplomaticAccordPicklistOptions(firstRaw);
        const relIdx = secondRaw != null ? Number(secondRaw) : 0;
        if (available.length > 0 && (!Number.isFinite(relIdx) || relIdx >= available.length)) {
          onBonusSelectionsPatch?.({ diplomaticAccordSecond: 0 });
        }
      }
      return;
    }

    if (isReactiveDoctrineCircle(def, def.level)) {
      playSoundEffect(EXPLORE_UNKNOWN_SELECT_SKILL_SOUND);
      onBonusSelectionsPatch?.({ [LEVEL6_OR7_PICK_GROUP]: 0 });
      onReactiveDoctrineConfigure?.();
      return;
    }

    if (isSecondAccordCircle(def, def.level)) {
      if (getLevel6Or7Pick(bonusSelections) !== 1) {
        playSoundEffect(EXPLORE_UNKNOWN_SELECT_SKILL_SOUND);
        onBonusSelectionsPatch?.({
          [LEVEL6_OR7_PICK_GROUP]: 1,
          diplomaticAccordSecond: bonusSelections?.diplomaticAccordSecond ?? 0,
        });
        return;
      }
      playSoundEffect(EXPLORE_UNKNOWN_SELECT_SKILL_SOUND);
      const nextIdx = nextCycleSelectIndex('diplomaticAccordSecond', bonusSelections);
      onBonusSelectionChange?.('diplomaticAccordSecond', nextIdx);
      return;
    }

    if (isAnchorVariantCircle(def)) {
      const sel = getAnchorVariantSelectionIndex(bonusSelections, def.groupId);
      const alreadySelected = sel != null && Number(sel) === Number(def.optionIndex);
      if (!alreadySelected) {
        playSoundEffect(EXPLORE_UNKNOWN_SELECT_SKILL_SOUND);
        onBonusSelectionChange?.(def.groupId, def.optionIndex);
        return;
      }
      const isAttached =
        def.refinedErraySlot && refinedErrayAttachments[def.bonusId] === true;
      const canAttachErray =
        def.refinedErraySlot &&
        typeof onAttachRefinedErray === 'function' &&
        (isAttached || refinedErraySpare > 0);
      if (canAttachErray) {
        playSoundEffect(EXPLORE_UNKNOWN_SLOT_ERRAY_SOUND);
        onAttachRefinedErray(def.bonusId);
        return;
      }
      onBonusSelectionChange?.(def.groupId, null);
      return;
    }

    if (isAltPathSelectTile(def) && isAltPathL21ErraySlotted(refinedErrayAttachments)) {
      if (def.optionIndex === 0) {
        if (typeof onAttachRefinedErray === 'function') {
          playSoundEffect(EXPLORE_UNKNOWN_SLOT_ERRAY_SOUND);
          onAttachRefinedErray(def.bonusId);
        }
        return;
      }
      if (!isAltPathSecondPickActive(def, bonusSelections)) {
        playSoundEffect(EXPLORE_UNKNOWN_SELECT_SKILL_SOUND);
        onBonusSelectionsPatch?.({
          [ALT_PATH_SELECT_GROUP]: 0,
          [ALT_PATH_SECOND_SELECT_KEY]: def.optionIndex,
        });
      }
      return;
    }

    if (def.type === 'variant' || def.type === 'selectOne') {
      if (!isSelectedInGroup(def)) {
        onBonusSelectionChange?.(def.groupId, def.optionIndex);
        return;
      }
      const isAttached =
        def.refinedErraySlot && refinedErrayAttachments[def.bonusId] === true;
      const canAttachErray =
        def.refinedErraySlot &&
        typeof onAttachRefinedErray === 'function' &&
        (isAttached || refinedErraySpare > 0);
      if (canAttachErray) {
        playSoundEffect(EXPLORE_UNKNOWN_SLOT_ERRAY_SOUND);
        onAttachRefinedErray(def.bonusId);
      }
      return;
    }

    const isAttached = def.refinedErraySlot && refinedErrayAttachments[def.bonusId] === true;
    const canAttach =
      def.refinedErraySlot &&
      typeof onAttachRefinedErray === 'function' &&
      (def.type === 'major' || def.type === 'loreArt') &&
      (isAttached || refinedErraySpare > 0);

    if (canAttach) {
      playSoundEffect(EXPLORE_UNKNOWN_SLOT_ERRAY_SOUND);
      onAttachRefinedErray(def.bonusId);
      return;
    }

    if (def.type === 'endgame') {
      onEndgameChange?.(def.bonusId);
      return;
    }

    if (def.type === 'optionalSkip') {
      const enabled = skippingBonuses[def.bonusId] !== false;
      onSkippingToggle?.(def.bonusId, !enabled);
    }
  };

  const isSelectableTile = (def, unlocked) => {
    if (!unlocked || !def) return false;
    if (isCycleSelectTile(def) || isReactiveDoctrineCircle(def, def.level) || isSecondAccordCircle(def, def.level)) {
      return true;
    }
    if (def.type === 'variant' || def.type === 'selectOne' || def.type === 'endgame' || def.type === 'optionalSkip') {
      return true;
    }
    if (def.refinedErraySlot && typeof onAttachRefinedErray === 'function') {
      return true;
    }
    if (
      isAltPathSelectTile(def) &&
      isAltPathL21ErraySlotted(refinedErrayAttachments) &&
      def.optionIndex > 0
    ) {
      return true;
    }
    return false;
  };

  return (
    <div className="eu-level-tree" aria-label={t('exploreUnknownPage.levelTree.regionLabel')}>
      {refinedErrayCount > 0 ? (
        <div className="eu-level-tree-refined-slot" aria-label={t('exploreUnknownPage.levelTree.refinedErray')}>
          <img src="/images/refined_erray.png" alt="" className="eu-level-tree-refined-img" draggable={false} />
          <span className="eu-level-tree-refined-label">
            {t('exploreUnknownPage.levelTree.refinedStock', {
              count: refinedErrayCount,
              spare: refinedErraySpare,
            })}
          </span>
        </div>
      ) : null}

      <div className="eu-level-tree-header">
        <div className="eu-level-tree-level-badge">
          <span className="eu-level-tree-level-badge-label">LV</span>
          <span className="eu-level-tree-level-badge-value">{level}</span>
        </div>
        <div className="eu-level-tree-header-text">
          <span className="eu-level-tree-title">{t('exploreUnknownPage.levelTree.title')}</span>
          <span className="eu-level-tree-xp-total">
            {t('exploreUnknownPage.levelTree.totalXp', { xp: totalXpDisplay })}
          </span>
          <div
            className="eu-level-tree-bar"
            role="progressbar"
            aria-valuenow={xpIntoLevel}
            aria-valuemin={0}
            aria-valuemax={xpIntoLevel + xpToNext || xpIntoLevel}
          >
            <div className="eu-level-tree-bar-fill" style={{ width: `${xpPct}%` }} />
          </div>
          {xpToNext > 0 ? (
            <span className="eu-level-tree-xp-next">
              {t('exploreUnknownPage.levelTree.xpToNext', {
                current: Number(xpIntoLevel || 0).toLocaleString(),
                needed: Number((xpIntoLevel || 0) + (xpToNext || 0)).toLocaleString(),
              })}
            </span>
          ) : (
            <span className="eu-level-tree-xp-max">{t('exploreUnknownPage.levelTree.maxLevel')}</span>
          )}
        </div>
      </div>

      <div className="eu-level-tree-board-scroll" ref={boardScrollRef}>
        <div className="eu-level-tree-board" style={boardStyle}>
          <svg
            className="eu-level-tree-path-svg"
            width={boardW}
            height={boardH}
            viewBox={`0 0 ${boardW} ${boardH}`}
            aria-hidden
          >
            <defs>
              <linearGradient id="eu-tree-path-locked" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(148, 163, 184, 0.35)" />
                <stop offset="100%" stopColor="rgba(71, 85, 105, 0.25)" />
              </linearGradient>
              <linearGradient id="eu-tree-path-unlocked" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fde68a" />
                <stop offset="50%" stopColor="#facc15" />
                <stop offset="100%" stopColor="#f59e0b" />
              </linearGradient>
            </defs>
            <path
              d={path}
              fill="none"
              stroke="url(#eu-tree-path-locked)"
              strokeWidth={pathStrokeLocked}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="2 6"
            />
            {lastUnlockedIdx >= 0 ? (
              <path
                d={buildTreePath(centers, Math.min(lastUnlockedIdx, FINALE_PATH_MAX_LEVEL_INDEX))}
                fill="none"
                stroke="url(#eu-tree-path-unlocked)"
                strokeWidth={pathStrokeUnlocked}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : null}
          </svg>

          {LEVELS.map((lv, i) => {
            const { x, y } = centers[i];
            const def = defsByLevel[String(lv)] || {};
            const meta = milestoneBonuses[String(lv)] || def;
            const unlocked = level >= lv;
            const isNext = i === nextIdx;
            const type = def.type || 'major';
            const shapeType = isAnchorMajorLevel(lv) ? 'major' : type;
            const active = isActiveBonus(def, unlocked, lv);
            const selectionForPreview = unlocked ? bonusSelections : {};
            let displayDef = def;
            let secondAccordPickI18nKey = null;
            if (isPrepAnchor(def, lv)) {
              displayDef =
                resolveAnchorVariantDisplayDef('prepBonus', defsByLevel, selectionForPreview) || def;
            } else if (isCycleSelectTile(def)) {
              const pick = resolveCycleSelectPick(def.groupId, selectionForPreview);
              if (pick) {
                displayDef = {
                  ...def,
                  icon: pick.icon,
                  i18nKey: pick.i18nKey,
                  bonusName: pick.bonusName || def.bonusName,
                };
              }
            } else if (isSecondAccordCircle(def, lv)) {
              const pick = resolveSecondAccordPick(selectionForPreview);
              if (pick) {
                displayDef = { ...def, icon: pick.icon };
                secondAccordPickI18nKey = pick.i18nKey;
              }
            }
            const anchorI18nKey = def.i18nKey || meta.i18nKey || `levelBonus${lv}`;
            const displayI18nKey = displayDef.i18nKey || meta.i18nKey || `levelBonus${lv}`;
            const titleI18nKey = isSecondAccordCircle(def, lv) ? anchorI18nKey : displayI18nKey;
            const slottedI18nKey = def.i18nKey || meta.i18nKey || `levelBonus${lv}`;
            const title = t(`exploreUnknownPage.${titleI18nKey}.title`, {
              defaultValue: (isSecondAccordCircle(def, lv) ? def : displayDef).bonusName || meta.bonusId || '',
            });
            const secondAccordActive =
              isSecondAccordCircle(def, lv) && getLevel6Or7Pick(selectionForPreview) === 1;
            const descriptionI18nKey = isPrepAnchor(def, lv)
              ? anchorI18nKey
              : secondAccordActive && secondAccordPickI18nKey
                ? secondAccordPickI18nKey
                : isSecondAccordCircle(def, lv)
                  ? anchorI18nKey
                  : displayI18nKey;
            const description = t(`exploreUnknownPage.${descriptionI18nKey}.description`, {
              defaultValue: '',
            });
            const isAttachedToTile = def.refinedErraySlot && refinedErrayAttachments[def.bonusId] === true;
            const erraySlotLabels = def.refinedErraySlot
              ? resolveExploreUnknownErraySlotLabels(def, t, slottedI18nKey)
              : null;
            const effectiveSlotLabel = erraySlotLabels
              ? isAttachedToTile
                ? erraySlotLabels.slottedLabel
                : erraySlotLabels.baseLabel
              : null;
            const titleParts = replaceFirstMetricInTitle(title, effectiveSlotLabel);
            const tooltipSide = x > boardW / 2 ? 'left' : 'right';
            const icon =
              type === 'optionalSkip'
                ? resolveOptionalSkipTileIcon(displayDef)
                : displayDef.icon || meta.icon;
            const selectable = isSelectableTile(def, unlocked);
            const canAttachAfterSelect =
              def.refinedErraySlot &&
              unlocked &&
              typeof onAttachRefinedErray === 'function' &&
              (type === 'major' ||
                type === 'loreArt' ||
                ((type === 'variant' || type === 'selectOne') && isSelectedInGroup(def))) &&
              (isAttachedToTile || refinedErraySpare > 0);
            const altPathSecondPickMode =
              isAltPathSelectTile(def) && isAltPathL21ErraySlotted(refinedErrayAttachments);
            const altPathSecondPickActive =
              altPathSecondPickMode && isAltPathSecondPickActive(def, bonusSelections);

            const tileSize = tileSizeForDef(def, boardLayout.tile, lv, boardLayout);
            const cornerBadge = resolveLevelTreeCornerBadge(def, meta);
            const showCornerBadge = unlocked && cornerBadge != null;
            const cornerIconPx = cornerIconSize(tileSize);
            const healthPlusIconPx = cornerIconSize(tileSize, 0.17);
            const lorePenIconPx = Math.max(8, Math.round(tileSize * 0.16));

            return (
              <div
                key={lv}
                className={[
                  'eu-level-tree-tile',
                  shapeClassForType(shapeType),
                  (isAnchorVariantCircle(def) ||
                    isReactiveDoctrineCircle(def, lv) ||
                    isSecondAccordCircle(def, lv)) &&
                  unlocked &&
                  !active
                    ? 'eu-level-tree-tile--choice-unselected'
                    : '',
                  unlocked ? 'eu-level-tree-tile--unlocked' : 'eu-level-tree-tile--locked',
                  active ? 'eu-level-tree-tile--active' : '',
                  isNext ? 'eu-level-tree-tile--next' : '',
                  selectable ? 'eu-level-tree-tile--selectable' : '',
                  def.refinedErraySlot ? 'eu-level-tree-tile--erray-slot' : '',
                  isAttachedToTile ? 'eu-level-tree-tile--erray-attached' : '',
                  lv === 19 ? 'eu-level-tree-tile--l19' : '',
                  type === 'endgame' && unlocked && !active ? 'eu-level-tree-tile--endgame-idle' : '',
                  type === 'endgame' && unlocked && active ? 'eu-level-tree-tile--endgame-selected' : '',
                  lv === CAPSTONE_LEVEL ? 'eu-level-tree-tile--capstone' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  left: `${x - tileSize / 2}px`,
                  top: `${y - tileSize / 2}px`,
                  width: `${tileSize}px`,
                  height: `${tileSize}px`,
                  '--eu-tile': `${tileSize}px`,
                }}
                onMouseEnter={isMobile ? undefined : () => setHovered(lv)}
                onMouseLeave={isMobile ? undefined : () => setHovered((cur) => (cur === lv ? null : cur))}
                onFocus={isMobile ? undefined : () => setHovered(lv)}
                onBlur={isMobile ? undefined : () => setHovered((cur) => (cur === lv ? null : cur))}
                onClick={() => handleTileActivate(def, unlocked)}
                onKeyDown={(e) => {
                  if (selectable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleTileActivate(def, unlocked);
                  }
                }}
                tabIndex={unlocked ? (selectable ? 0 : -1) : 0}
                role={unlocked && selectable ? 'button' : 'img'}
                aria-label={`${title} — ${t('exploreUnknownPage.levelTree.regionLabel')} ${lv}`}
              >
                <div
                  className={[
                    'eu-level-tree-tile-frame',
                    type === 'endgame' ? 'eu-level-tree-tile-frame--endgame' : '',
                    type === 'optionalSkip' ? 'eu-level-tree-tile-frame--skip' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {type === 'endgame' ? (
                    <EndgameStarTileArt
                      icon={icon}
                      patternId={displayDef.bonusId || `endgame-${lv}`}
                    />
                  ) : type === 'optionalSkip' ? (
                    <SkipWorldTileArt
                      icon={icon}
                      patternId={displayDef.bonusId || `skip-${lv}`}
                    />
                  ) : icon ? (
                    <img
                      key={displayDef.bonusId || lv}
                      src={icon}
                      alt=""
                      className="eu-level-tree-tile-icon"
                      draggable={false}
                    />
                  ) : (
                    <div className="eu-level-tree-tile-icon eu-level-tree-tile-icon--placeholder" />
                  )}
                  {showCornerBadge && cornerBadge === 'chance' ? (
                    <span
                      className="eu-level-tree-tile-corner-badge eu-level-tree-tile-corner-badge--chance"
                      aria-hidden
                    >
                      <FaPercent size={cornerIconPx} />
                    </span>
                  ) : null}
                  {showCornerBadge && cornerBadge === 'boost' ? (
                    <span
                      className="eu-level-tree-tile-corner-badge eu-level-tree-tile-corner-badge--boost"
                      aria-hidden
                    >
                      <HiArrowTrendingUp size={cornerIconPx} />
                    </span>
                  ) : null}
                  {showCornerBadge && cornerBadge === 'auto' ? (
                    <span
                      className="eu-level-tree-tile-corner-badge eu-level-tree-tile-corner-badge--auto"
                      aria-hidden
                    >
                      <GiGears size={cornerIconPx} />
                    </span>
                  ) : null}
                  {showCornerBadge && cornerBadge === 'health' ? (
                    <span
                      className="eu-level-tree-tile-corner-badge eu-level-tree-tile-corner-badge--health"
                      aria-hidden
                    >
                      <span className="eu-level-tree-tile-health-pluses">
                        <FaPlus size={healthPlusIconPx} />
                        <FaPlus size={healthPlusIconPx} />
                        <FaPlus size={healthPlusIconPx} />
                      </span>
                    </span>
                  ) : null}
                  {type === 'loreArt' ? (
                    <span className="eu-level-tree-tile-lore-pen" aria-hidden>
                      <FaPen size={lorePenIconPx} />
                    </span>
                  ) : null}
                </div>
                {def.refinedErraySlot ? (
                  <span className="eu-level-tree-erray-pocket" aria-hidden>
                    {isAttachedToTile ? (
                      <img
                        src="/images/refined_erray.png"
                        alt=""
                        className="eu-level-tree-erray-pocket-img"
                        draggable={false}
                      />
                    ) : null}
                  </span>
                ) : null}
                {shapeType === 'major' ? <div className="eu-level-tree-tile-badge">{lv}</div> : null}
                {!isMobile && hovered === lv ? (
                  <div
                    className={`eu-level-tree-tooltip eu-level-tree-tooltip--${tooltipSide}`}
                    role="tooltip"
                  >
                    <span className="eu-level-tree-tooltip-level">
                      {t('exploreUnknownPage.levelTree.regionLabel')} · {lv}
                    </span>
                    <>
                      {titleParts.highlight ? (
                        <strong>
                          {titleParts.before}
                          <span className="eu-level-tree-tooltip-percent">{titleParts.highlight}</span>
                          {titleParts.after}
                        </strong>
                      ) : (
                        <strong>{title}</strong>
                      )}
                      <span>{description}</span>
                      {erraySlotLabels ? (
                        <span className="eu-level-tree-tooltip-erray-values">
                          <span>
                            {t('exploreUnknownPage.levelTree.baseBonusLine', {
                              bonus: erraySlotLabels.baseLabel,
                            })}
                          </span>
                          <span>
                            {t('exploreUnknownPage.levelTree.slottedBonusLine', {
                              bonus: erraySlotLabels.slottedLabel,
                            })}
                          </span>
                        </span>
                      ) : null}
                      {!unlocked ? (
                        <span className="eu-level-tree-tooltip-hint">
                          {t('exploreUnknownPage.levelTree.lockedBonus', { level: lv })}
                        </span>
                      ) : null}
                      {unlocked && isAnchorVariantCircle(def) && (!active || !canAttachAfterSelect) ? (
                          <span className="eu-level-tree-tooltip-hint">
                            {!active
                              ? t('exploreUnknownPage.levelTree.clickToSelectVariantCircle')
                              : t('exploreUnknownPage.levelTree.clickToDeselectVariantCircle')}
                          </span>
                        ) : null}
                      {unlocked && isAnchorVariantAnchor(def, lv) ? (
                        <span className="eu-level-tree-tooltip-hint">
                          {getAnchorVariantSelectionIndex(bonusSelections, 'prepBonus') != null
                            ? t('exploreUnknownPage.levelTree.clickAnchorToClearVariant')
                            : t('exploreUnknownPage.levelTree.anchorVariantHint')}
                        </span>
                      ) : null}
                      {unlocked && isCycleSelectTile(def) ? (
                        <span className="eu-level-tree-tooltip-hint">
                          {t('exploreUnknownPage.levelTree.clickToCycleDiplomaticAccord')}
                        </span>
                      ) : null}
                      {unlocked && isSecondAccordCircle(def, lv) ? (
                        <span className="eu-level-tree-tooltip-hint">
                          {getLevel6Or7Pick(bonusSelections) === 1
                            ? t('exploreUnknownPage.levelTree.clickToCycleDiplomaticAccord')
                            : t('exploreUnknownPage.levelTree.clickToSelectSecondAccord')}
                        </span>
                      ) : null}
                      {unlocked && isReactiveDoctrineCircle(def, lv) ? (
                        <span className="eu-level-tree-tooltip-hint">
                          {getLevel6Or7Pick(bonusSelections) === 0
                            ? reactiveDoctrineMetadata?.faction_id && reactiveDoctrineMetadata?.strategy_id
                              ? t('exploreUnknownPage.levelTree.reactiveDoctrineConfigured')
                              : t('exploreUnknownPage.levelTree.clickToConfigureReactiveDoctrine')
                            : t('exploreUnknownPage.levelTree.clickToSelectReactiveDoctrine')}
                        </span>
                      ) : null}
                      {unlocked &&
                      (type === 'variant' || type === 'selectOne') &&
                      !isAnchorVariantCircle(def) &&
                      !isCycleSelectTile(def) &&
                      !isSecondAccordCircle(def, lv) &&
                      !isReactiveDoctrineCircle(def, lv) &&
                      !altPathSecondPickMode &&
                      (!active || !canAttachAfterSelect) ? (
                          <span className="eu-level-tree-tooltip-hint">
                            {active
                              ? t('exploreUnknownPage.levelTree.selectedBonus')
                              : t('exploreUnknownPage.levelTree.clickToSelect')}
                          </span>
                        ) : null}
                      {unlocked && altPathSecondPickMode && def.optionIndex > 0 ? (
                        <span className="eu-level-tree-tooltip-hint">
                          {altPathSecondPickActive
                            ? t('exploreUnknownPage.levelTree.selectedSecondAltPathBonus')
                            : t('exploreUnknownPage.levelTree.clickToSelectSecondAltPathBonus')}
                        </span>
                      ) : null}
                      {unlocked && type === 'endgame' ? (
                        <span className="eu-level-tree-tooltip-hint">
                          {active
                            ? t('exploreUnknownPage.levelTree.selectedEndgame')
                            : t('exploreUnknownPage.levelTree.clickToSelectEndgame')}
                        </span>
                      ) : null}
                      {unlocked && type === 'optionalSkip' ? (
                        <span className="eu-level-tree-tooltip-hint">
                          {active
                            ? t('exploreUnknownPage.levelTree.skipEnabled')
                            : t('exploreUnknownPage.levelTree.skipDisabled')}
                        </span>
                      ) : null}
                      {canAttachAfterSelect ? (
                        <span className="eu-level-tree-tooltip-attach">
                          {isAttachedToTile
                            ? t('exploreUnknownPage.levelTree.detachRefined')
                            : t('exploreUnknownPage.levelTree.attachRefined')}
                        </span>
                      ) : null}
                    </>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
